import { Controller } from "@hotwired/stimulus"

// Manages individual event detail popovers (toggle, inline edit, dismiss).
// One instance per event pill/block.
export default class extends Controller {
  static targets = ["popover", "popoverDetail", "popoverForm"]

  connect() {
    this._closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    document.addEventListener("click", this._closeOnOutsideClick)
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutsideClick)
    document.removeEventListener("keydown", this._onKeydown)
  }

  _onKeydown(event) {
    if (event.key !== "Escape") return
    // A confirm dialog inside the popover handles its own Escape. Without this
    // the key backs out of the dialog and closes the popover underneath it in
    // one press, so cancelling a delete loses the round you were looking at.
    if (document.querySelector("dialog[open]")) return
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) return
    event.stopPropagation()
    this.popoverTarget.classList.add("hidden")
    this._showDetail()
  }

  // ── Popover toggle ──────────────────────────────────────────────────

  togglePopover(event) {
    event.stopPropagation()
    const popover = this.popoverTarget
    const wasHidden = popover.classList.contains("hidden")

    // Close all other popovers first
    document
      .querySelectorAll("[data-calendar-target='popover']")
      .forEach((el) => {
        el.classList.add("hidden")
      })

    if (wasHidden) {
      this._showDetail()
      popover.classList.remove("hidden")
      this._positionPopover(event.currentTarget, popover)
    }
  }

  closePopover(event) {
    event.stopPropagation()
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add("hidden")
      this._showDetail()
    }
  }

  closeOnOutsideClick(event) {
    if (!this.hasPopoverTarget) return
    if (this.element.contains(event.target)) return
    this.popoverTarget.classList.add("hidden")
    this._showDetail()
  }

  // ── Inline edit inside the popover ──────────────────────────────────

  async editEvent(event) {
    event.stopPropagation()
    const url = event.params.editUrl
    if (!url) return

    const response = await fetch(url, {
      headers: {
        Accept: "text/html",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
    })

    if (!response.ok) return

    const html = await response.text()
    this._showForm(html)
  }

  // Called by the form's Cancel / X buttons when editing inside an event popover
  dismissForm(event) {
    event.stopPropagation()

    if (
      this.hasPopoverFormTarget &&
      !this.popoverFormTarget.classList.contains("hidden")
    ) {
      this._showDetail()
      this._repositionCurrentPopover()
      return
    }
  }

  // ── Group selector in new-event form (also used when calendar controller is on the popover) ──

  updateFormAction(event) {
    const groupId = event.target.value
    const form = event.target
      .closest("[data-controller='recurrence-picker']")
      ?.querySelector("form")
    if (!form) return

    const action = form.getAttribute("action")
    const newAction = action.replace(
      /\/friend_groups\/\d+\//,
      `/friend_groups/${groupId}/`,
    )
    form.setAttribute("action", newAction)
  }

  // ── Private ─────────────────────────────────────────────────────────

  _showDetail() {
    if (this.hasPopoverDetailTarget) {
      this.popoverDetailTarget.classList.remove("hidden")
    }
    if (this.hasPopoverFormTarget) {
      this.popoverFormTarget.classList.add("hidden")
      this.popoverFormTarget.innerHTML = ""
    }
    if (this.hasPopoverTarget) {
      this.popoverTarget.style.width = ""
      this.popoverTarget.style.maxHeight = ""
      this.popoverTarget.style.overflowY = ""
    }
  }

  _showForm(html) {
    if (this.hasPopoverDetailTarget) {
      this.popoverDetailTarget.classList.add("hidden")
    }
    if (this.hasPopoverFormTarget) {
      this.popoverFormTarget.innerHTML = html
      this.popoverFormTarget.classList.remove("hidden")
    }
    if (this.hasPopoverTarget) {
      this.popoverTarget.style.width = "24rem"
      this.popoverTarget.style.maxHeight = `${window.innerHeight - 16}px`
      this.popoverTarget.style.overflowY = "auto"
      this._repositionCurrentPopover()
    }
  }

  _repositionCurrentPopover() {
    if (!this.hasPopoverTarget) return
    const popover = this.popoverTarget
    const trigger = this.element.querySelector("[data-action*='togglePopover']")
    if (trigger) {
      this._positionPopover(trigger, popover)
    }
  }

  _positionPopover(trigger, popover) {
    const triggerRect = trigger.getBoundingClientRect()
    const padding = 8

    popover.style.visibility = "hidden"
    popover.style.left = "0px"
    popover.style.top = "0px"

    const popoverWidth = popover.offsetWidth
    const popoverHeight = popover.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let top = triggerRect.bottom + 4
    let left = triggerRect.left

    if (top + popoverHeight + padding > viewportHeight) {
      top = triggerRect.top - popoverHeight - 4
    }
    if (top < padding) {
      top = padding
    }
    if (left + popoverWidth + padding > viewportWidth) {
      left = viewportWidth - popoverWidth - padding
    }
    if (left < padding) {
      left = padding
    }

    popover.style.left = `${left}px`
    popover.style.top = `${top}px`
    popover.style.visibility = ""
  }
}
